import { PublicLayout } from "@/components/PublicLayout";
import { SEOHead } from "@/components/SEOHead";
import { ModeBadge } from "@/components/figures/ModeBadge";
import { RHTPClock } from "@/components/rhtp/RHTPClock";
import { initiatives, initiativesNote } from "@/data/rhtp";

function formatMillions(value: number) {
  return `$${(value / 1_000_000).toFixed(1)}M`;
}

const budgetPeriods = [
  {
    period: "Budget Period 1",
    window: "December 2025 to October 2026",
    expenditures: "to September 2027",
  },
  {
    period: "Budget Period 2",
    window: "October 2026 to October 2027",
    expenditures: "to September 2028",
  },
  {
    period: "Budget Period 3",
    window: "October 2027 to October 2028",
    expenditures: "to September 2029",
  },
];

const federalRules = [
  "Administrative costs at or below 10 percent",
  "Provider payments at or below 15 percent of each budget period and only for items insurers do not pay for",
  "Capital at or below 20 percent, existing rural facilities, renovation and equipment, no new construction",
  "No supplanting of existing funding",
  "Workforce funds for rural ZIP codes only",
];

const capacityTest = [
  "Does it create measurable rural capacity?",
  "Does it make an existing rural provider more capable?",
  "Does it reduce the work required to deliver the same care?",
  "Does it reduce travel or delay for the patient?",
  "Does it connect rather than duplicate?",
  "Is there a credible path for it to remain after the funding ends?",
];

const capacityUnits = [
  "Appointment capacity",
  "Rural clinician-hours",
  "Days to appointment",
  "Services available locally",
  "Referrals completed",
  "Clinician administrative hours per encounter",
  "After-hours capacity",
  "Off-island trips avoided",
];

const instructions = [
  {
    title: "Preserve capacity",
    body: "Keep the rural clinics and screening services that exist today from closing while the program spends.",
  },
  {
    title: "Build capacity",
    body: "Add clinicians, hours, and services where the shortage is measured, not where the paperwork is easiest.",
  },
  {
    title: "Connect capacity",
    body: "Spend on connection only where it removes work from clinicians or makes care possible that was not.",
  },
  {
    title: "Measure capacity",
    body: "Publish a baseline by island before the first federal reporting cycle, and measure against it.",
  },
];

const asks = [
  "Adopt the Capacity Test as the common evaluation framework now.",
  "Commission the Medical Economic Evaluation the state's own briefing names as an independent measurement function, with a public baseline by island offered at no cost, and if the function is institutionalized it is a state procurement in which the Task Force is not the vendor.",
  "Design the Budget Period 3 portfolio now around the gaps the state's briefing names: palliative care, community mental health and substance use, obstetrics, workforce housing, and sub-specialist access.",
];

const record = [
  {
    name: "Hawaiʻi Healthcare Task Force",
    href: "https://hawaiihealthcaretaskforce.org",
    body: "The Task Force's RHTP position and documents are published there as they are released.",
  },
  {
    name: "Hawaiʻi Rural Health Transformation Program",
    href: "https://rhtp.hawaii.gov",
    body: "The state program office.",
  },
  {
    name: "CMS Rural Health Transformation Program",
    href: "https://www.medicaid.gov/resources-for-states/rural-health",
    body: "Federal program page.",
  },
];

const RhtpPage = () => {
  return (
    <PublicLayout>
      <SEOHead
        title="RHTP · Buy capacity, not contracts · David Isei"
        description="Hawaiʻi's Rural Health Transformation Program award, initiative by initiative, with the Rural Health Capacity Test used to judge every dollar."
        url="https://davidisei.com/rhtp"
      />

      <div className="mx-auto max-w-4xl px-5 py-16 sm:px-8 sm:py-20">
        {/* 1. Hero */}
        <section>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-liberty-gold">
            Rural Health Transformation Program
          </p>
          <h1 className="mt-3 font-display text-4xl font-bold leading-tight text-foreground sm:text-5xl">
            Buy capacity, not contracts.
          </h1>
          <p className="mt-6 text-base leading-relaxed text-foreground/85">
            Hawaiʻi's Rural Health Transformation Program award is the largest
            single investment in rural care the state has seen. My work on it,
            with Esther Y. Smith, MD, comes down to one question asked of every
            dollar: after this investment, will rural Hawaiʻi have more usable
            healthcare capacity than today, particularly primary care, and will
            it remain after the funding ends?
          </p>
          <div className="mt-8 rounded-xl border border-liberty-gold/50 bg-card/40 p-5 sm:p-6">
            <p className="text-sm leading-relaxed text-foreground/90">
              The Task Force is not an applicant for RHTP funds and asks for
              none. It convenes and makes the money deployable; it asks for no
              grant, no contract, and no administrative role.
            </p>
          </div>
        </section>

        {/* 2. Clock */}
        <section className="mt-14">
          <RHTPClock />
        </section>

        {/* 3. What the program is */}
        <section className="mt-16">
          <h2 className="font-display text-3xl font-bold text-foreground">
            What the program is
          </h2>
          <p className="mt-5 text-base leading-relaxed text-foreground/85">
            Public Law 119-21 created the program. Funds flow from CMS through
            its Office of Rural Health Transformation to the Office of the
            Governor and the Department of Budget and Finance, and then to the
            Hawaiʻi Rural Health Transformation Program, which coordinates six
            initiatives across five state agencies and the University of
            Hawaiʻi.
          </p>

          <div className="mt-8 grid gap-5 sm:grid-cols-2">
            {initiatives.map((item) => (
              <article
                key={item.key}
                className="rounded-xl border border-border/60 bg-card/60 p-5"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="font-display text-lg font-bold text-foreground">
                    {item.key}
                  </h3>
                  <span className="font-mono text-sm text-liberty-gold">
                    {formatMillions(item.bp1)}
                  </span>
                </div>
                <p className="mt-1 text-sm text-foreground/80">{item.name}</p>
                <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  Lead: {item.lead}
                </p>
                <p className="mt-3 text-[13px] leading-relaxed text-foreground/80">
                  {item.what}
                </p>
              </article>
            ))}
          </div>

          <p className="mt-4 text-[12px] italic text-muted-foreground">
            {initiativesNote}
          </p>

          <div className="mt-10 overflow-x-auto">
            <table className="w-full min-w-[560px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-border/60">
                  <th className="py-2 pr-4 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                    Period
                  </th>
                  <th className="py-2 pr-4 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                    Obligation window
                  </th>
                  <th className="py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                    Expenditures
                  </th>
                </tr>
              </thead>
              <tbody>
                {budgetPeriods.map((row) => (
                  <tr key={row.period} className="border-b border-border/40">
                    <td className="py-3 pr-4 font-medium text-foreground">
                      {row.period}
                    </td>
                    <td className="py-3 pr-4 text-foreground/80">{row.window}</td>
                    <td className="py-3 text-foreground/80">
                      {row.expenditures}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 className="mt-10 font-display text-xl font-bold text-foreground">
            Federal rules the state briefing lists
          </h3>
          <ul className="mt-4 space-y-2">
            {federalRules.map((rule) => (
              <li
                key={rule}
                className="flex gap-3 text-sm leading-relaxed text-foreground/85"
              >
                <span className="text-liberty-gold">·</span>
                {rule}
              </li>
            ))}
          </ul>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <p className="text-[12px] leading-relaxed text-muted-foreground">
              Source: Hawaiʻi RHTP briefing to the Task Force, September 2026;
              CMS Rural Health Transformation Program materials
            </p>
            <ModeBadge mode="DIRECT" />
          </div>
        </section>

        {/* 4. Capacity Test */}
        <section className="mt-16">
          <h2 className="font-display text-3xl font-bold text-foreground">
            The Rural Health Capacity Test
          </h2>
          <p className="mt-5 text-base leading-relaxed text-foreground/85">
            Capacity is the amount of necessary care a system can reliably
            deliver. It is not organizations, contracts, facilities, clinicians,
            or platforms on paper. The test asks the same question of every
            proposal, in six parts:
          </p>
          <ol className="mt-6 space-y-3">
            {capacityTest.map((q, i) => (
              <li
                key={q}
                className="flex gap-4 rounded-lg border border-border/60 bg-card/40 p-4 text-sm leading-relaxed text-foreground/85"
              >
                <span className="font-mono text-liberty-gold">{i + 1}</span>
                {q}
              </li>
            ))}
          </ol>

          <div className="mt-8 rounded-xl border border-liberty-gold/50 bg-card/40 p-5 sm:p-6">
            <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-liberty-gold">
              The negative question
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground/90">
              Could this project meet every contractual deliverable without
              improving patient access? If yes, the metrics are wrong.
            </p>
          </div>

          <h3 className="mt-10 font-display text-xl font-bold text-foreground">
            How proposals are rated
          </h3>
          <div className="mt-4 flex flex-wrap gap-3">
            {["Demonstrated", "Claimed", "Not addressed"].map((pill) => (
              <span
                key={pill}
                className="inline-flex items-center rounded-full border border-border/70 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.12em] text-foreground/80"
              >
                {pill}
              </span>
            ))}
          </div>
          <p className="mt-3 text-sm text-foreground/80">
            Described, never scored, and published with sources.
          </p>

          <h3 className="mt-10 font-display text-xl font-bold text-foreground">
            Units of capacity
          </h3>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {capacityUnits.map((unit) => (
              <li
                key={unit}
                className="flex gap-3 text-sm leading-relaxed text-foreground/85"
              >
                <span className="text-liberty-gold">·</span>
                {unit}
              </li>
            ))}
          </ul>

          <p className="mt-6 text-sm leading-relaxed text-foreground/85">
            Training counts, licenses, platforms, committees, and encounter
            volume are process measures. They are fine. They are not capacity.
          </p>
          <p className="mt-4 text-[12px] text-muted-foreground">
            Developed by David Isei and Esther Y. Smith, MD. Offered without
            restriction, with attribution.
          </p>
        </section>

        {/* 5. Four instructions */}
        <section className="mt-16">
          <h2 className="font-display text-3xl font-bold text-foreground">
            Four instructions
          </h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            {instructions.map((card) => (
              <article
                key={card.title}
                className="rounded-xl border border-border/60 bg-card/60 p-5"
              >
                <h3 className="font-display text-lg font-bold text-foreground">
                  {card.title}
                </h3>
                <p className="mt-3 text-[13px] leading-relaxed text-foreground/80">
                  {card.body}
                </p>
              </article>
            ))}
          </div>
        </section>

        {/* 6. Asks */}
        <section className="mt-16">
          <h2 className="font-display text-3xl font-bold text-foreground">
            What the Task Force has asked the state to do
          </h2>
          <ol className="mt-6 space-y-4">
            {asks.map((ask, i) => (
              <li
                key={ask}
                className="flex gap-4 rounded-lg border border-border/60 bg-card/40 p-5 text-sm leading-relaxed text-foreground/85"
              >
                <span className="font-mono text-liberty-gold">{i + 1}</span>
                {ask}
              </li>
            ))}
          </ol>
        </section>

        {/* 7. Institutional record */}
        <section className="mt-16">
          <h2 className="font-display text-3xl font-bold text-foreground">
            The institutional record
          </h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-3">
            {record.map((item) => (
              <a
                key={item.href}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-xl border border-border/60 bg-card/60 p-5 transition-colors hover:border-liberty-gold/60"
              >
                <h3 className="font-display text-base font-bold text-liberty-gold">
                  {item.name}
                </h3>
                <p className="mt-3 text-[13px] leading-relaxed text-foreground/80">
                  {item.body}
                </p>
              </a>
            ))}
          </div>
        </section>

      </div>
    </PublicLayout>
  );
};

export default RhtpPage;
